import { useState, useMemo, useRef } from "react";
import { debounce } from "lodash";
import Icons from "@/assets";
import { useNavigate } from "react-router-dom";
import {
    Modal,
    Box,
    Typography,
    IconButton,
    Button,
    Rating,
    TextField,
    CircularProgress,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import useApiClient from "@/api/Client";
import ENDPOINTS from "@/api/endpoints";
import { toast } from "react-toastify";
import { getApiErrorMessage } from "@/utils/helper";

interface NavigationFeedbackModalProps {
    open: boolean;
    onClose: () => void;
    navigationHistoryId: string | number;
}

const NavigationFeedbackModal = ({
    open,
    onClose,
    navigationHistoryId,
}: NavigationFeedbackModalProps) => {
    const navigate = useNavigate();
    const { api } = useApiClient();

    const [rating, setRating] = useState<number | null>(null);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleClose = () => {
        if (submitting) return;
        onClose();
    };

    const handleSubmit = async () => {
        if (!rating) {
            toast.error("Please select a rating.");
            return;
        }
        if (!comment.trim()) {
            toast.error("Please enter your feedback.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await api.post(
                ENDPOINTS.NAVIGATE.FEEDBACK(navigationHistoryId),
                { rating, feedback_string: comment.trim() },
            );
            if (res?.data?.status === 200) {
                toast.success("Feedback submitted. Thank you!");
                onClose();
                localStorage.setItem("profileTab", "recentlyVisited");
                navigate("/dashboard", { replace: true });
            } else {
                toast.error(res?.data?.message || "Failed to submit feedback.");
            }
        } catch (err) {
            console.error("Feedback error:", err);
            toast.error(getApiErrorMessage(err, "Something went wrong. Please try again."));
        } finally {
            setSubmitting(false);
        }
    };

    // Use a ref to always point to the latest handleSubmit function
    const handleSubmitRef = useRef(handleSubmit);
    handleSubmitRef.current = handleSubmit;

    // Create a stable debounced function that calls the latest handleSubmit
    const debouncedSubmit = useMemo(
        () =>
            debounce(() => handleSubmitRef.current(), 5000, {
                leading: true,
                trailing: false,
            }),
        [],
    );

    return (
        <Modal open={open} onClose={handleClose}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100vh",
                    px: "4%",
                }}
            >
                <Box
                    sx={{
                        backgroundColor: "#fff",
                        borderRadius: "20px",
                        p: "6%",
                        width: "100%",
                        maxWidth: 420,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                        position: "relative",
                    }}
                >
                    {/* Close button */}
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            backgroundColor: "#9443DF",
                            color: "#fff",
                            width: 28,
                            height: 28,
                            "&:hover": { backgroundColor: "#7a35b5" },
                        }}
                        size="small"
                    >
                        <Close sx={{ fontSize: 16 }} />
                    </IconButton>

                    {/* Icon */}
                    <Box sx={{ display: "flex", justifyContent: "center", mb: "4%" }}>
                        <Box
                            component="img"
                            src={Icons.NavigationFeedbackIcon}
                            alt="Navigation feedback"
                            sx={{ width: { xs: "18vw", sm: 72 }, height: { xs: "18vw", sm: 72 }, objectFit: "contain" }}
                        />
                    </Box>

                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: "4.2vw", sm: 17 },
                            textAlign: "center",
                            color: "#191919",
                            mb: "2%",
                        }}
                    >
                        How was your Navigation experience?
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: { xs: "3.2vw", sm: 13 },
                            textAlign: "center",
                            color: "#666",
                            mb: "5%",
                        }}
                    >
                        Your feedback help us improve and serve you better
                    </Typography>

                    {/* Star rating */}
                    <Box sx={{ display: "flex", justifyContent: "center", mb: "5%" }}>
                        <Rating
                            value={rating}
                            onChange={(_, v) => setRating(v)}
                            size="large"
                            sx={{
                                "& .MuiSvgIcon-root": {
                                    color: "#F2B12F",
                                    fontSize: { xs: "9vw", sm: 36 },
                                },
                            }}
                        />
                    </Box>

                    {/* Comment */}
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Tell us more"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        variant="outlined"
                        inputProps={{ maxLength: 249 }}
                        sx={{
                            mb: "5%",
                            "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                        }}
                    />

                    {/* Submit */}
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={debouncedSubmit}
                        disabled={submitting}
                        startIcon={
                            submitting
                                ? <CircularProgress size={16} sx={{ color: "#fff" }} />
                                : undefined
                        }
                        sx={{
                            borderRadius: "30px",
                            py: "3%",
                            fontSize: { xs: "3.88vw", sm: 15 },
                            fontWeight: 700,
                            backgroundColor: "#7747FB",
                            textTransform: "none",
                            boxShadow: "none",
                            "&:hover": { backgroundColor: "#6635e0", boxShadow: "none" },
                            "&.Mui-disabled": { backgroundColor: "#C9A8F5", color: "#fff" },
                        }}
                    >
                        {submitting ? "Submitting..." : "Submit"}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default NavigationFeedbackModal;
